<template>
  <div class="kosher-certificates-list">
    <PrintLocModal
      v-if="modalVisible"
      :plantList="plants"
      :selectedProductIds="modalProductIds"
      :open="modalVisible"
      :companySummary="main.companySummary!"
      :label-type="labelType"
      :total-products="totalProducts"
      @closeModal="onPrintModalClosed"
    />
    <div class="certificates-list__actions">
      <Button
        theme-color="primary"
        @click.prevent="printSelectedProductsClicked"
        :disabled="selectedProductIds.length === 0"
        data-cy="print-selected-products-button"
      >
        DOWNLOAD {{ selectedProductIds.length }} SELECTED LOC
      </Button>
      <Button
        theme-color="primary"
        backgroundColor="#fff"
        @click.prevent="printLocAllClicked"
        data-cy="print-all-products-button"
      >
        DOWNLOAD ALL LOC
      </Button>
      <Button theme-color="secondary" @click.prevent="selectAllClicked"
        >SELECT ALL PRODUCTS</Button
      >
      <Button @click.prevent="clearSelectionClicked" class="clear-btn"
        >CLEAR SELECTION</Button
      >
    </div>
    <div v-if="products.length == 0 && !loading" class="no-results">
      <span>No results found</span>
    </div>
    <ListView
      class="kosher-certificates-list-view"
      :data-items="products"
      item="certificateTile"
      footer="certificateListFooter"
      @scroll="scrollHandler($event as unknown as Event)"
      data-cy="kosher-certificates-list"
    >
      <template v-slot:certificateTile="{ props }">
        <KosherCertificatesTile
          :product="props.dataItem"
          @selectionChanged="onSelectionChanged"
        />
      </template>
      <template v-slot:certificateListFooter>
        <Loader
          v-if="loading"
          class="certificates-loader"
          size="large"
          type="pulsing"
          data-cy="prodcertificatesucts-loader"
        />
      </template>
    </ListView>
  </div>
</template>

<script setup lang="ts">
import KosherCertificatesTile from "./KosherCertificatesTile.vue";
import { computed, onBeforeMount, ref, watch } from "vue";
import { ListView } from "@progress/kendo-vue-listview";
import { Loader } from "@progress/kendo-vue-indicators";
import { EnumPrintLocType } from "@/Domain/EnumPrintLocType";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import type {
  GetProductDisplayByCriteriaWithFilteringRequest,
  ProductWithSelection,
} from "@/api/kosherCertificates";
import type { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import type { IdAndName } from "@/Domain/IdAndName";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { Button } from "@progress/kendo-vue-buttons";
import PrintLocModal from "./PrintLocModal.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";

const main = useMainStore();

const props = defineProps<{
  plants: IdAndName[];
  labelType: EnumPrintLocType;
  daysBack: number;
  searchValue: string;
}>();

const emit = defineEmits<{
  (e: "productsChanged", skip: number, take: number): void;
}>();

const take = ref<number>(10);
const skip = ref<number>(0);
const loading = ref<boolean>(false);
const totalProducts = ref<number>(0);
const products = ref<ProductWithSelection[]>([]);
const selectedProductIds = ref<string[]>([]);

const allProductIds = ref<string[]>([]);
const allProductIdsLoading = ref<boolean>(false);

const modalVisible = ref<boolean>(false);
const modalProductIds = ref<string[]>([]);

const loadScrollThreshold = 100;

const productsIdsRequest = computed(() => {
  return {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: props.searchValue,
    labelType:
      Object.keys(EnumPrintLocType)[
        Object.values(EnumPrintLocType).indexOf(props.labelType)
      ],
    plantList: props.plants.map((p) => p.ID).join(";"),
    daysBack: props.daysBack,
    groupByLabel: true,
    filters: "",
    skip: 0,
    take: 999999,
    sortField: "",
    ascending: false,
    valuesForFieldOnly: "VueFullUkdId",
  };
});

const scrollHandler = async function (e: Event) {
  const target = e.target as HTMLDivElement | null;
  if (!target) return;
  if (
    target.scrollTop + loadScrollThreshold <
    target.scrollHeight - target.clientHeight
  )
    return;
  if (skip.value + take.value >= totalProducts.value) return;
  if (loading.value) return;

  skip.value = skip.value + take.value;
  await searchMatchingProductsAsync(null, true);
  emit("productsChanged", skip.value, take.value);
};

const getProductDisplayAsync = async function (
  filter: CompositeFilterDescriptor | null = null,
  valuesForFieldOnly: string = ""
) {
  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: props.searchValue || "",
    labelType:
      Object.keys(EnumPrintLocType)[
        Object.values(EnumPrintLocType).indexOf(props.labelType)
      ],
    plantList: props.plants.map((p) => p.ID).join(";"),
    daysBack: props.daysBack,
    groupByLabel: true,
    filters: "",
    skip: skip.value,
    take: take.value,
    sortField: "",
    ascending: false,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetProductDisplayByCriteriaWithFilteringRequest;

  const endpoint = "GetProductDisplayByCriteriaWithFiltering";

  return api.get(endpoint, requestParams);
};

const searchMatchingProductsAsync = async function (
  filter: CompositeFilterDescriptor | null = null,
  appendProducts: boolean = false
) {
  if (loading.value) return;
  loading.value = true;

  // Show no products if no plants selected
  if (props.plants.length === 0) {
    products.value = [];
    totalProducts.value = 0;
    loading.value = false;
    return;
  }

  await handleResponsePromiseAsync(
    getProductDisplayAsync(filter),
    (data) => {
      const newProducts = data.ListItems;
      newProducts.forEach((p) => {
        p.selected = selectedProductIds.value.some((sp) => sp === p.AgencyId);
      });
      if (!appendProducts) products.value = data.ListItems;
      else products.value = [...products.value, ...data.ListItems];
      totalProducts.value = data.TotalItemsInList;
    },
    "KosherCertificatesView",
    "searchMatchingProductsAsync"
  );

  loading.value = false;
};

const getAllProductsIdsAsync = async function () {
  if (allProductIdsLoading.value) return;

  allProductIds.value = [];
  allProductIdsLoading.value = true;

  const productPromise = CacheService.getResponseAsync(
    createCacheKey(productsIdsRequest.value, "ProductDisplayIds"),
    () =>
      api.get(
        "GetProductDisplayByCriteriaWithFilteringValuesForFieldOnly",
        productsIdsRequest.value
      )
  );

  await handleResponsePromiseAsync(
    productPromise,
    (data) => {
      allProductIds.value = data.ListItems;
    },
    "KosherCertificatesList",
    "getAllProductsIdsAsync"
  );

  allProductIdsLoading.value = false;
};

const onSelectionChanged = function (product: ProductWithSelection) {
  const index = selectedProductIds.value.findIndex(
    (p) => p === product.AgencyId
  );
  if (product.selected) {
    if (index === -1) selectedProductIds.value.push(product.AgencyId);
    else
      console.debug(
        `KosherCertificatesList.vue: onSelectionChanged(): product already selected`,
        product,
        selectedProductIds.value[index]
      );
  } else if (index > -1) {
    selectedProductIds.value.splice(index, 1);
  }
};

const printSelectedProductsClicked = function () {
  modalProductIds.value = selectedProductIds.value;
  modalVisible.value = true;
};

const printLocAllClicked = function () {
  modalProductIds.value = [];
  modalVisible.value = true;
};

const selectAllClicked = function () {
  selectedProductIds.value = [...allProductIds.value];
  products.value.forEach((p) => {
    p.selected = true;
  });
};

const clearSelectionClicked = function () {
  selectedProductIds.value = [];
  products.value.forEach((p) => {
    p.selected = false;
  });
};

const onPrintModalClosed = function () {
  // 12875: Grid Row Selection(s) Should Be Removed After Printing LOC
  selectedProductIds.value = [];
  products.value.forEach((p) => {
    p.selected = false;
  });
  modalVisible.value = false;
};

watch(
  () => [props.plants, props.daysBack, props.searchValue],
  () => {
    skip.value = 0;
    products.value = [];
    selectedProductIds.value = [];
    totalProducts.value = 0;

    searchMatchingProductsAsync();
    getAllProductsIdsAsync();
  }
);

onBeforeMount(() => {
  searchMatchingProductsAsync();
  getAllProductsIdsAsync();
});
</script>

<style lang="scss">
.kosher-certificates-list {
  margin: 0;
}

.certificates-list__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 20px 20px;

  .k-button {
    flex: 1;
  }
}

.kosher-certificates-list-view {
  background: none !important;
  border: none !important;
  max-height: calc(100vh - 350px);

  .k-listview-content {
    padding: 10px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .k-listview-footer {
    border-top-width: 0 !important;
  }
}

.certificates-loader {
  display: flex !important;
  justify-content: center;
  margin: 32px 0;
}

.no-results {
  display: flex;
  justify-content: center;
  margin: 32px 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: #6c757d;
}
.clear-btn {
  background: none;
  border: 1px solid #3a5da6;
  color: #3a5da6;
}
</style>
